import { useEffect, useState } from "react";
import {
  FolderOpen, Upload, Loader2, X, Download, Trash2, Link2, Copy, Send, Briefcase,
  MessageCircle, Eye, Plus, Clock, Ban, CheckCircle2, FileText, Film, ExternalLink, RotateCcw, Pencil, Cloud, QrCode,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { uploadFileChunked } from "@/lib/chunkUpload";
import { resolveMediaUrl } from "@/components/FileUpload";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { SharePointBrowser } from "@/components/SharePointBrowser";
import { SharePointPickerDialog } from "@/components/SharePointPickerDialog";
import { DropLinkManager } from "@/components/DropLinkManager";
import { QRDialog } from "@/components/QRDialog";

export function fmtSize(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

async function uploadToLibrary(file, onProgress, projectId = "") {
  const data = await uploadFileChunked(file, { onProgress });
  await api.post("/library/files", { file_id: data.id, project_id: projectId });
  return data;
}

function FileThumb({ f, className = "h-14 w-14" }) {
  if (f.kind === "image")
    return <img src={resolveMediaUrl(f.url)} alt="" className={`${className} rounded-lg object-cover bg-neutral-100 border border-neutral-200 shrink-0`} />;
  if (f.kind === "video")
    return (
      <div className={`${className} rounded-lg bg-neutral-900 border border-neutral-200 shrink-0 flex items-center justify-center text-white/80`}>
        <Film className="h-5 w-5" />
      </div>
    );
  return (
    <div className={`${className} rounded-lg bg-neutral-100 border border-neutral-200 shrink-0 flex items-center justify-center text-neutral-400`}>
      <FileText className="h-5 w-5" />
    </div>
  );
}

function ChatShareDialog({ file, onClose }) {
  const [convs, setConvs] = useState([]);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/chat/conversations").then(({ data }) => setConvs(data)).catch(() => {}); }, []);
  const share = async (c) => {
    setBusy(c.conv);
    try {
      await api.post("/chat/messages", {
        conv: c.conv, text: "", mentions: [],
        attachment: { file_id: file.id, url: file.url, kind: file.kind, filename: file.filename },
      });
      toast.success(`Im Chat geteilt (${c.type === "team" ? "Team-Kanal" : c.name})`);
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Teilen fehlgeschlagen"); }
    finally { setBusy(""); }
  };
  return (
    <div className="fixed inset-0 z-[10005] flex items-end sm:items-center justify-center" data-testid="file-chat-share-dialog">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[70vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
          <div className="text-sm font-bold flex items-center gap-2"><MessageCircle className="h-4 w-4 text-orange-600" /> Im Chat teilen</div>
          <button data-testid="file-chat-share-close" onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-neutral-100 flex items-center justify-center text-neutral-500"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-4 py-2 text-xs text-neutral-500 truncate border-b border-neutral-50">{file.filename}</div>
        <div className="flex-1 overflow-y-auto p-2">
          {convs.map((c) => (
            <button key={c.conv} data-testid={`file-share-conv-${c.conv}`} onClick={() => share(c)} disabled={!!busy}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-neutral-50 text-left disabled:opacity-50">
              <UserAvatar src={c.avatar_url} name={c.type === "team" ? "Team" : c.name} color="#F8991D" className="h-9 w-9" />
              <span className="text-sm font-medium flex-1 truncate">{c.type === "team" ? "Team-Kanal" : c.name}</span>
              {busy === c.conv ? <Loader2 className="h-4 w-4 animate-spin text-orange-600" /> : <Send className="h-4 w-4 text-neutral-300" />}
            </button>
          ))}
          {convs.length === 0 && <p className="text-sm text-neutral-400 text-center py-6">Lade Unterhaltungen…</p>}
        </div>
      </div>
    </div>
  );
}

function ProjectBar({ projects, selected, onSelect, onChanged, isAdmin }) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [client, setClient] = useState("");
  const [contact, setContact] = useState("");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) return toast.error("Projektname erforderlich");
    setSaving(true);
    try {
      const { data } = await api.post("/projects", { name, client_name: client, contact });
      toast.success("Projekt angelegt");
      setName(""); setClient(""); setContact(""); setCreating(false);
      onChanged();
      onSelect(data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Anlegen fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  const current = projects.find((p) => p.id === selected);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({ name: "", client_name: "", contact: "" });

  const startEdit = () => {
    setEdit({ name: current.name, client_name: current.client_name || "", contact: current.contact || "" });
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!edit.name.trim()) return toast.error("Projektname erforderlich");
    try {
      await api.patch(`/projects/${current.id}`, edit);
      toast.success("Projekt aktualisiert");
      setEditing(false);
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen"); }
  };

  const toggleStatus = async () => {
    try {
      await api.patch(`/projects/${current.id}`, { status: current.status === "done" ? "active" : "done" });
      toast.success(current.status === "done" ? "Projekt reaktiviert" : "Projekt abgeschlossen");
      onChanged();
    } catch { toast.error("Aktion fehlgeschlagen"); }
  };

  const removeProject = async () => {
    try {
      await api.delete(`/projects/${current.id}`);
      toast.success("Projekt gelöscht — Dateien bleiben unter „Allgemein“ erhalten");
      onSelect("all");
      onChanged();
    } catch { toast.error("Löschen fehlgeschlagen"); }
  };

  return (
    <div className="mb-4">
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1" data-testid="project-bar">
        {[["all", "Alle"], ["", "Allgemein"]].map(([id, label]) => (
          <button key={id || "general"} data-testid={`project-chip-${id || "general"}`} onClick={() => onSelect(id)}
            className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition ${selected === id ? "bg-orange-500 border-orange-500 text-white" : "bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300"}`}>
            {label}
          </button>
        ))}
        {projects.map((p) => (
          <button key={p.id} data-testid={`project-chip-${p.id}`} onClick={() => onSelect(p.id)}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition ${selected === p.id ? "bg-orange-500 border-orange-500 text-white" : "bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300"} ${p.status === "done" ? "opacity-60" : ""}`}>
            <Briefcase className="h-3 w-3" /> {p.name}
            {p.status === "done" && <CheckCircle2 className="h-3 w-3" />}
          </button>
        ))}
        <button data-testid="project-new-btn" onClick={() => setCreating((v) => !v)}
          className="shrink-0 inline-flex items-center gap-1 px-3.5 py-1.5 rounded-full text-xs font-semibold border border-dashed border-orange-300 text-orange-600 hover:bg-orange-50 transition">
          <Plus className="h-3 w-3" /> Projekt
        </button>
      </div>

      {creating && (
        <div className="mt-2 bg-white border border-orange-300 rounded-xl p-3 space-y-2" data-testid="project-create-card">
          <div className="grid sm:grid-cols-3 gap-2">
            <input data-testid="project-name-input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Projektname *" className="bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2 text-sm" />
            <input data-testid="project-client-input" value={client} onChange={(e) => setClient(e.target.value)}
              placeholder="Kunde (Firma)" className="bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2 text-sm" />
            <input data-testid="project-contact-input" value={contact} onChange={(e) => setContact(e.target.value)}
              placeholder="Ansprechpartner / E-Mail" className="bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="flex gap-2">
            <button data-testid="project-save-btn" onClick={create} disabled={saving}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg bg-orange-600 text-white disabled:opacity-50">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Anlegen
            </button>
            <button onClick={() => setCreating(false)} className="text-xs font-medium px-3 py-2 rounded-lg text-neutral-500 hover:bg-neutral-100">Abbrechen</button>
          </div>
        </div>
      )}

      {current && (
        <div className="mt-2 bg-white border border-neutral-200 rounded-xl px-3.5 py-2.5" data-testid="project-info">
          {editing ? (
            <div className="space-y-2" data-testid="project-edit-form">
              <div className="grid sm:grid-cols-3 gap-2">
                <input data-testid="project-edit-name" value={edit.name} onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Projektname *" className="bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2 text-sm" />
                <input data-testid="project-edit-client" value={edit.client_name} onChange={(e) => setEdit((p) => ({ ...p, client_name: e.target.value }))}
                  placeholder="Kunde (Firma)" className="bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2 text-sm" />
                <input data-testid="project-edit-contact" value={edit.contact} onChange={(e) => setEdit((p) => ({ ...p, contact: e.target.value }))}
                  placeholder="Ansprechpartner / E-Mail" className="bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex gap-2">
                <button data-testid="project-edit-save" onClick={saveEdit}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg bg-orange-600 text-white">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Speichern
                </button>
                <button onClick={() => setEditing(false)} className="text-xs font-medium px-3 py-2 rounded-lg text-neutral-500 hover:bg-neutral-100">Abbrechen</button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
              <div className="min-w-0">
                <p className="text-xs font-bold flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5 text-orange-600" /> {current.name}</p>
                <p className="text-[11px] text-neutral-400">
                  {current.client_name || "Kein Kunde hinterlegt"}{current.contact ? ` · ${current.contact}` : ""} · {current.file_count} Dateien · {current.share_count} Freigaben
                </p>
              </div>
              <div className="ml-auto flex items-center gap-1.5">
                <button data-testid="project-edit-btn" onClick={startEdit} title="Projekt bearbeiten"
                  className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1.5 rounded-lg bg-neutral-100 text-neutral-600 hover:bg-neutral-200">
                  <Pencil className="h-3 w-3" /> Bearbeiten
                </button>
                <button data-testid="project-toggle-status" onClick={toggleStatus}
                  className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1.5 rounded-lg bg-neutral-100 text-neutral-600 hover:bg-neutral-200">
                  {current.status === "done" ? <><RotateCcw className="h-3 w-3" /> Reaktivieren</> : <><CheckCircle2 className="h-3 w-3" /> Abschließen</>}
                </button>
                {isAdmin && (
                  <button data-testid="project-delete" onClick={removeProject} title="Projekt löschen"
                    className="h-7 w-7 rounded-lg hover:bg-rose-50 flex items-center justify-center text-neutral-400 hover:text-rose-600">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AblageTab({ user, projectId, projects, onProjectsChanged }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [shareFile, setShareFile] = useState(null);
  const load = () => api.get("/library/files").then(({ data }) => { setFiles(data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const visible = files.filter((f) => projectId === "all" || (f.project_id || "") === projectId);
  const projectName = (id) => projects.find((p) => p.id === id)?.name;

  const onPick = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const target = projectId === "all" ? "" : projectId;
    let ok = 0;
    for (let i = 0; i < picked.length; i++) {
      const f = picked[i];
      if (f.size > 500 * 1024 * 1024) { toast.error(`${f.name}: zu groß (max. 500 MB)`); continue; }
      try {
        setUploading({ name: f.name, idx: i + 1, total: picked.length, pct: 0 });
        await uploadToLibrary(f, (pct) => setUploading({ name: f.name, idx: i + 1, total: picked.length, pct }), target);
        ok += 1;
      } catch (err) {
        toast.error(`${f.name}: ${formatApiErrorDetail(err.response?.data?.detail) || `Upload fehlgeschlagen (${err.response?.status || "Netzwerkfehler"})`}`);
      }
    }
    setUploading(null);
    if (ok > 0) toast.success(ok === 1 ? "Datei in der Ablage" : `${ok} Dateien in der Ablage`);
    load();
    onProjectsChanged();
  };

  const moveProject = async (f, pid) => {
    try {
      await api.post(`/library/files/${f.id}/project`, { project_id: pid });
      toast.success(pid ? `Verschoben nach „${projectName(pid)}“` : "Nach „Allgemein“ verschoben");
      load();
      onProjectsChanged();
    } catch { toast.error("Verschieben fehlgeschlagen"); }
  };

  const remove = async (f) => {
    try {
      await api.delete(`/library/files/${f.id}`);
      toast.success("Aus Ablage entfernt");
      load();
      onProjectsChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Entfernen fehlgeschlagen"); }
  };

  const canDelete = (f) => user?.role === "admin" || f.uploaded_by === String(user?.id);

  const [dlBusy, setDlBusy] = useState(null);
  const downloadFile = async (f) => {
    if (dlBusy) return;
    setDlBusy({ id: f.id, pct: 0 });
    try {
      const { data: blob } = await api.get(f.url.replace(/^\/api/, ""), {
        responseType: "blob",
        timeout: 600000,
        onDownloadProgress: (e) => {
          if (e.total) setDlBusy({ id: f.id, pct: Math.round((e.loaded / e.total) * 100) });
        },
      });
      const name = f.filename || "datei";
      const saveViaAnchor = () => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(u), 30000);
        toast.success(`„${name}“ heruntergeladen ✅`);
      };
      const isMobile = window.matchMedia?.("(pointer: coarse)")?.matches;
      const file = new File([blob], name, { type: blob.type || "application/octet-stream" });
      if (isMobile && typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file] });
          toast.success("Im Teilen-Menü »Sichern« wählen — fertig ✅");
        } catch (err) {
          if (err?.name === "AbortError") return;
          saveViaAnchor();
        }
      } else {
        saveViaAnchor();
      }
    } catch {
      toast.error("Download fehlgeschlagen");
    } finally {
      setDlBusy(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="ablage-tab">
      <label className="block bg-white border-2 border-dashed border-neutral-300 rounded-xl p-4 sm:p-6 text-center cursor-pointer hover:border-orange-400 transition">
        <input type="file" multiple hidden onChange={onPick} data-testid="library-upload-input" />
        {uploading ? (
          <span className="inline-flex items-center gap-2 text-sm font-medium text-orange-600">
            <Loader2 className="h-4 w-4 animate-spin" /> {uploading.name} ({uploading.idx}/{uploading.total}) — {uploading.pct}%
          </span>
        ) : (
          <>
            <Upload className="h-6 w-6 text-neutral-400 mx-auto" />
            <p className="text-sm font-medium text-neutral-700 mt-1.5">
              Dateien hochladen{projectId && projectId !== "all" ? ` → ${projectName(projectId) || "Projekt"}` : ""}
            </p>
            <p className="text-xs text-neutral-400 mt-0.5">Videos, Fotos, Dokumente — max. 500 MB je Datei</p>
          </>
        )}
      </label>

      {loading ? (
        <div className="py-12 flex justify-center text-neutral-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : visible.length === 0 ? (
        <p className="text-sm text-neutral-400 text-center py-10">Keine Dateien{projectId !== "all" ? " in diesem Bereich" : " in der Ablage"}.</p>
      ) : (
        <div className="space-y-2">
          {visible.map((f) => (
            <div key={f.id} data-testid={`library-file-${f.id}`}
              className="bg-white border border-neutral-200 rounded-xl p-3 grid grid-cols-[3rem_minmax(0,1fr)] gap-x-3 gap-y-2 sm:flex sm:items-center sm:gap-3">
              <FileThumb f={f} className="h-12 w-12 sm:h-14 sm:w-14" />
              <div className="min-w-0 flex-1 self-center">
                <p className="text-[13px] sm:text-sm font-semibold leading-snug line-clamp-2 break-words sm:truncate">{f.filename || "Unbenannt"}</p>
                <p className="text-[11px] text-neutral-400 mt-0.5 leading-relaxed">
                  <span className="whitespace-nowrap">{fmtSize(f.size)}</span>
                  <span className="hidden sm:inline"> · </span>
                  <span className="block sm:inline truncate">{f.uploader_name || "—"}</span>
                  <span className="hidden sm:inline"> · </span>
                  <span className="block sm:inline whitespace-nowrap">{fmtDate(f.created_at)}</span>
                </p>
                <select
                  data-testid={`library-project-select-${f.id}`}
                  value={f.project_id || ""}
                  onChange={(e) => moveProject(f, e.target.value)}
                  className="mt-1.5 text-[10px] font-medium text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-md px-2 py-1 max-w-full sm:max-w-[160px]"
                >
                  <option value="">Allgemein</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="col-span-2 flex items-center gap-1.5 pt-2 border-t border-neutral-100 sm:col-auto sm:pt-0 sm:border-t-0 sm:gap-1 sm:shrink-0">
                {(f.kind === "image" || f.kind === "video") && (
                  <a href={resolveMediaUrl(f.url)} target="_blank" rel="noreferrer" data-testid={`library-view-${f.id}`}
                    title="Ansehen" className="h-9 flex-1 sm:flex-none sm:w-9 rounded-lg bg-neutral-50 sm:bg-transparent hover:bg-neutral-100 flex items-center justify-center gap-1.5 text-neutral-600">
                    <Eye className="h-4 w-4" /><span className="text-[11px] font-medium sm:hidden">Ansehen</span>
                  </a>
                )}
                <button onClick={() => downloadFile(f)} data-testid={`library-download-${f.id}`}
                  disabled={!!dlBusy}
                  title="Herunterladen / Sichern" className="h-9 flex-1 sm:flex-none sm:min-w-9 px-1.5 rounded-lg bg-neutral-50 sm:bg-transparent hover:bg-neutral-100 flex items-center justify-center gap-1.5 text-neutral-600 disabled:opacity-50">
                  {dlBusy?.id === f.id ? (
                    <span className="flex items-center gap-1 text-[10px] font-bold text-orange-600" data-testid={`library-download-progress-${f.id}`}>
                      <Loader2 className="h-4 w-4 animate-spin" />{dlBusy.pct > 0 ? `${dlBusy.pct}%` : ""}
                    </span>
                  ) : (
                    <><Download className="h-4 w-4" /><span className="text-[11px] font-medium sm:hidden">Sichern</span></>
                  )}
                </button>
                <button onClick={() => setShareFile(f)} data-testid={`library-chat-share-${f.id}`}
                  title="Im Chat teilen" className="h-9 flex-1 sm:flex-none sm:w-9 rounded-lg bg-orange-50 sm:bg-transparent hover:bg-orange-50 flex items-center justify-center gap-1.5 text-orange-700 sm:text-orange-600">
                  <MessageCircle className="h-4 w-4" /><span className="text-[11px] font-semibold sm:hidden">Chat</span>
                </button>
                {canDelete(f) && (
                  <button onClick={() => remove(f)} data-testid={`library-delete-${f.id}`}
                    title="Entfernen" aria-label={`${f.filename || "Datei"} entfernen`}
                    className="h-9 w-9 shrink-0 rounded-lg bg-neutral-50 sm:bg-transparent hover:bg-rose-50 flex items-center justify-center text-neutral-400 hover:text-rose-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {shareFile && <ChatShareDialog file={shareFile} onClose={() => setShareFile(null)} />}
    </div>
  );
}

function CreateShareCard({ libraryFiles, projectId, projects, onCreated, onCancel, onLibraryChanged }) {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [expires, setExpires] = useState(0);
  const [selected, setSelected] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [saving, setSaving] = useState(false);
  const [spOpen, setSpOpen] = useState(false);

  const target = projectId === "all" ? "" : projectId;
  const visibleFiles = projectId === "all" ? libraryFiles : libraryFiles.filter((f) => (f.project_id || "") === projectId);
  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const onPick = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of picked) {
      try {
        setUploading({ name: f.name, pct: 0 });
        const data = await uploadToLibrary(f, (pct) => setUploading({ name: f.name, pct }), target);
        setSelected((p) => [...p, data.id]);
      } catch (err) { toast.error(`${f.name}: ${formatApiErrorDetail(err.response?.data?.detail) || `Upload fehlgeschlagen (${err.response?.status || "Netzwerkfehler"})`}`); }
    }
    setUploading(null);
    onLibraryChanged();
  };

  const create = async () => {
    if (!title.trim()) return toast.error("Titel erforderlich (z. B. Kundenname/Projekt)");
    if (selected.length === 0) return toast.error("Mindestens eine Datei auswählen");
    setSaving(true);
    try {
      const { data } = await api.post("/shares", { title, note, file_ids: selected, expires_days: expires, project_id: target });
      onCreated(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erstellen fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-orange-300 rounded-xl p-4 space-y-3" data-testid="create-share-card">
      <div className="flex items-center justify-between">
        <h3 className="font-head font-semibold text-[15px] flex items-center gap-1.5">
          <Link2 className="h-4 w-4 text-orange-600" /> Neue Kunden-Freigabe
          {target && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-700">{projects.find((p) => p.id === target)?.name}</span>}
        </h3>
        <button onClick={onCancel} data-testid="create-share-cancel" className="text-neutral-400 hover:text-neutral-700"><X className="h-4 w-4" /></button>
      </div>
      <input data-testid="share-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
        placeholder="Titel (z. B. Videos für Bäckerei Müller)" className="w-full bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2.5 text-sm" />
      <textarea data-testid="share-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
        placeholder="Notiz an den Kunden (optional)" className="w-full bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2.5 text-sm resize-none" />
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5 flex items-center gap-1"><Clock className="h-3 w-3" /> Link gültig</p>
        <div className="flex gap-2">
          {[[0, "Unbegrenzt"], [7, "7 Tage"], [30, "30 Tage"]].map(([d, l]) => (
            <button key={d} data-testid={`share-expiry-${d}`} onClick={() => setExpires(d)}
              className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition ${expires === d ? "bg-orange-500 border-orange-500 text-white" : "bg-neutral-50 border-neutral-200 text-neutral-600"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">Dateien auswählen ({selected.length})</p>
        <div className="flex gap-2 mb-2">
          <label className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-neutral-300 text-xs font-medium text-neutral-600 cursor-pointer hover:border-orange-400">
            <input type="file" multiple hidden onChange={onPick} data-testid="share-upload-input" />
            {uploading ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {uploading.name} — {uploading.pct}%</> : <><Plus className="h-3.5 w-3.5" /> Neue Datei hochladen</>}
          </label>
          <button
            data-testid="share-sharepoint-btn"
            onClick={() => setSpOpen(true)}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-sky-300 text-xs font-medium text-sky-700 hover:border-sky-500 hover:bg-sky-50"
          >
            <Cloud className="h-3.5 w-3.5" /> Aus SharePoint
          </button>
        </div>
        <SharePointPickerDialog
          open={spOpen}
          onClose={() => setSpOpen(false)}
          projectId={target}
          onPicked={(att) => {
            setSelected((p) => (p.includes(att.file_id) ? p : [...p, att.file_id]));
            onLibraryChanged();
            toast.success(`„${att.filename}" aus SharePoint übernommen und ausgewählt ✅`);
          }}
        />
        <div className="max-h-52 overflow-y-auto space-y-1.5">
          {visibleFiles.map((f) => (
            <button key={f.id} data-testid={`share-select-${f.id}`} onClick={() => toggle(f.id)}
              className={`w-full flex items-center gap-2.5 p-2 rounded-lg border text-left transition ${selected.includes(f.id) ? "border-orange-400 bg-orange-50" : "border-neutral-200 bg-white hover:bg-neutral-50"}`}>
              <FileThumb f={f} className="h-9 w-9" />
              <span className="text-xs font-medium truncate flex-1">{f.filename}</span>
              <span className="text-[10px] text-neutral-400 shrink-0">{fmtSize(f.size)}</span>
              {selected.includes(f.id) && <CheckCircle2 className="h-4 w-4 text-orange-600 shrink-0" />}
            </button>
          ))}
          {visibleFiles.length === 0 && <p className="text-xs text-neutral-400 text-center py-3">Keine passenden Dateien — lade oben eine hoch.</p>}
        </div>
      </div>
      <button data-testid="share-create-btn" onClick={create} disabled={saving}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-orange-600 text-white font-semibold text-sm disabled:opacity-50">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Freigabe-Link erstellen
      </button>
    </div>
  );
}

function ApprovalSummary({ files }) {
  const approved = files.filter((f) => f.approval?.decision === "approved");
  const changes = files.filter((f) => f.approval?.decision === "changes");
  if (!approved.length && !changes.length) return null;
  return (
    <div className="mt-2 space-y-1" data-testid="share-approvals">
      {files.filter((f) => f.approval).map((f) => (
        <div key={f.file_id} className={`text-[11px] rounded-lg px-2.5 py-1.5 ${f.approval.decision === "approved" ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}>
          {f.approval.decision === "approved" ? "🟢 Freigegeben" : "🟠 Änderungswunsch"}: <span className="font-semibold">{f.filename}</span>
          {f.approval.client_name && ` — ${f.approval.client_name}`}
          {f.approval.comment && <span className="block mt-0.5 italic">„{f.approval.comment}"</span>}
        </div>
      ))}
    </div>
  );
}

function FreigabenTab({ projectId, projects }) {
  const [shares, setShares] = useState([]);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [qrShare, setQrShare] = useState(null);
  const [editingId, setEditingId] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editNote, setEditNote] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const load = () => Promise.all([api.get("/shares"), api.get("/library/files")])
    .then(([s, l]) => { setShares(s.data); setLibrary(l.data); setLoading(false); })
    .catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const visible = shares.filter((s) => projectId === "all" || (s.project_id || "") === projectId);
  const projectName = (id) => projects.find((p) => p.id === id)?.name;

  const copyLink = async (s) => {
    const url = `${window.location.origin}${s.url_path}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link kopiert — an den Kunden senden"); }
    catch { toast.info(url); }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/shares/${s.id}`, { is_active: !s.is_active });
      toast.success(s.is_active ? "Freigabe deaktiviert" : "Freigabe aktiviert");
      load();
    } catch { toast.error("Aktion fehlgeschlagen"); }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditTitle(s.title || "");
    setEditNote(s.note || "");
  };

  const cancelEdit = () => {
    setEditingId("");
    setEditTitle("");
    setEditNote("");
  };

  const saveEdit = async (s) => {
    if (!editTitle.trim()) return toast.error("Titel erforderlich");
    setEditSaving(true);
    try {
      await api.patch(`/shares/${s.id}`, { title: editTitle, note: editNote });
      toast.success("Kunden-Freigabe aktualisiert");
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bearbeiten fehlgeschlagen");
    } finally {
      setEditSaving(false);
    }
  };

  const remove = async (s) => {
    try { await api.delete(`/shares/${s.id}`); toast.success("Freigabe gelöscht"); load(); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  };

  if (loading) return <div className="py-12 flex justify-center text-neutral-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="freigaben-tab">
      {creating ? (
        <CreateShareCard
          libraryFiles={library}
          projectId={projectId}
          projects={projects}
          onCancel={() => setCreating(false)}
          onLibraryChanged={() => api.get("/library/files").then(({ data }) => setLibrary(data))}
          onCreated={(s) => { setCreating(false); load(); copyLink(s); }}
        />
      ) : (
        <button data-testid="new-share-btn" onClick={() => setCreating(true)}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-orange-300 bg-orange-500/10 text-orange-700 text-sm font-semibold hover:bg-orange-500/15 transition">
          <Plus className="h-4 w-4" /> Neue Kunden-Freigabe erstellen
        </button>
      )}

      {visible.length === 0 && !creating && (
        <p className="text-sm text-neutral-400 text-center py-10">Noch keine Freigaben{projectId !== "all" ? " in diesem Bereich" : ""}.</p>
      )}
      <QRDialog open={!!qrShare} onClose={() => setQrShare(null)} url={qrShare ? `${window.location.origin}${qrShare.url_path}` : ""} title={qrShare?.title || "Freigabe"} />
      {visible.map((s) => {
        const status = !s.is_active ? ["Deaktiviert", "bg-neutral-200 text-neutral-600"] : s.expired ? ["Abgelaufen", "bg-rose-100 text-rose-700"] : ["Aktiv", "bg-emerald-100 text-emerald-700"];
        return (
          <div key={s.id} data-testid={`share-${s.id}`} className="bg-white border border-neutral-200 rounded-xl p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-head font-semibold text-[15px] truncate">{s.title}</h3>
                <p className="text-[11px] text-neutral-400 mt-0.5">
                  {s.project_id && projectName(s.project_id) && <span className="text-orange-600 font-semibold">{projectName(s.project_id)} · </span>}
                  {s.files.length} {s.files.length === 1 ? "Datei" : "Dateien"} · {fmtSize(s.files.reduce((a, f) => a + (f.size || 0), 0))} · erstellt {fmtDate(s.created_at)}
                  {s.expires_at && ` · gültig bis ${fmtDate(s.expires_at)}`}
                </p>
              </div>
              <span className={`shrink-0 text-[10px] font-bold px-2 py-1 rounded-full ${status[1]}`} data-testid={`share-status-${s.id}`}>{status[0]}</span>
            </div>
            {s.note && <p className="text-xs text-neutral-500 mt-1.5 line-clamp-2">{s.note}</p>}
            <ApprovalSummary files={s.files} />
            {editingId === s.id && (
              <div className="mt-3 rounded-xl border border-orange-200 bg-orange-50/60 p-3 space-y-2" data-testid={`share-edit-form-${s.id}`}>
                <div>
                  <label htmlFor={`share-edit-title-${s.id}`} className="block text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Titel</label>
                  <input id={`share-edit-title-${s.id}`} data-testid={`share-edit-title-${s.id}`} value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)} autoFocus
                    className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
                </div>
                <div>
                  <label htmlFor={`share-edit-note-${s.id}`} className="block text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Infotext für den Kunden</label>
                  <textarea id={`share-edit-note-${s.id}`} data-testid={`share-edit-note-${s.id}`} value={editNote}
                    onChange={(e) => setEditNote(e.target.value)} rows={3}
                    placeholder="Hinweise, Rückfragen oder Freigabeinformationen für den Kunden"
                    className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2.5 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-orange-300" />
                </div>
                <p className="text-[11px] text-neutral-500">Link, QR-Code und zugehörige Dateien bleiben unverändert.</p>
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                  <button type="button" onClick={cancelEdit} disabled={editSaving}
                    className="px-3 py-2 rounded-lg border border-neutral-200 bg-white text-xs font-semibold text-neutral-600 hover:bg-neutral-50 disabled:opacity-50">
                    Abbrechen
                  </button>
                  <button type="button" data-testid={`share-edit-save-${s.id}`} onClick={() => saveEdit(s)} disabled={editSaving}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-neutral-900 text-white text-xs font-semibold disabled:opacity-50">
                    {editSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Änderungen speichern
                  </button>
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-1.5 mt-3">
              <button data-testid={`share-copy-${s.id}`} onClick={() => copyLink(s)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-orange-500 text-white">
                <Copy className="h-3.5 w-3.5" /> Link kopieren
              </button>
              <button data-testid={`share-qr-${s.id}`} onClick={() => setQrShare(s)}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg bg-neutral-100 text-neutral-700 hover:bg-neutral-200">
                <QrCode className="h-3.5 w-3.5" /> QR
              </button>
              <a data-testid={`share-open-${s.id}`} href={s.url_path} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg bg-neutral-100 text-neutral-700 hover:bg-neutral-200">
                <ExternalLink className="h-3.5 w-3.5" /> Öffnen
              </a>
              <button data-testid={`share-edit-${s.id}`} onClick={() => editingId === s.id ? cancelEdit() : startEdit(s)}
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg transition ${editingId === s.id ? "bg-orange-100 text-orange-800" : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"}`}>
                <Pencil className="h-3.5 w-3.5" /> {editingId === s.id ? "Bearbeitung schließen" : "Bearbeiten"}
              </button>
              <button data-testid={`share-toggle-${s.id}`} onClick={() => toggleActive(s)}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg bg-neutral-100 text-neutral-700 hover:bg-neutral-200">
                {s.is_active ? <><Ban className="h-3.5 w-3.5" /> Deaktivieren</> : <><CheckCircle2 className="h-3.5 w-3.5" /> Aktivieren</>}
              </button>
              <button data-testid={`share-delete-${s.id}`} onClick={() => remove(s)}
                className="ml-auto h-8 w-8 rounded-lg hover:bg-rose-50 flex items-center justify-center text-neutral-400 hover:text-rose-600" title="Löschen">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function DateienPanel() {
  const { user } = useAuth();
  const [tab, setTab] = useState("ablage");
  const [dropLinksOpen, setDropLinksOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("all");
  const loadProjects = () => api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  useEffect(() => { loadProjects(); }, []);

  return (
    <div data-testid="dateien-panel">
      <ProjectBar projects={projects} selected={projectId} onSelect={setProjectId} onChanged={loadProjects} isAdmin={user?.role === "admin"} />
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        <button data-testid="dateien-tab-ablage" onClick={() => setTab("ablage")}
          className={`shrink-0 px-4 sm:px-5 py-2 rounded-lg text-sm font-semibold border transition ${tab === "ablage" ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-600 border-neutral-200"}`}>
          Ablage
        </button>
        <button data-testid="dateien-tab-freigaben" onClick={() => setTab("freigaben")}
          className={`shrink-0 px-4 sm:px-5 py-2 rounded-lg text-sm font-semibold border transition ${tab === "freigaben" ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-600 border-neutral-200"}`}>
          Kunden-Freigaben
        </button>
        <button data-testid="dateien-tab-sharepoint" onClick={() => setTab("sharepoint")}
          className={`shrink-0 px-4 sm:px-5 py-2 rounded-lg text-sm font-semibold border transition ${tab === "sharepoint" ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-600 border-neutral-200"}`}>
          SharePoint
        </button>
        <button data-testid="dateien-tab-archiv" onClick={() => setTab("archiv")}
          className={`shrink-0 px-4 sm:px-5 py-2 rounded-lg text-sm font-semibold border transition ${tab === "archiv" ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-600 border-neutral-200"}`}>
          Archiv
        </button>
        <button data-testid="dateien-droplinks-btn" onClick={() => setDropLinksOpen(true)}
          className="shrink-0 px-4 sm:px-5 py-2 rounded-lg text-sm font-semibold border transition bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100 flex items-center gap-1.5">
          <Link2 className="h-3.5 w-3.5" /> Drop-Links
        </button>
      </div>
      <DropLinkManager open={dropLinksOpen} onClose={() => setDropLinksOpen(false)} />
      {tab === "ablage"
        ? <AblageTab user={user} projectId={projectId} projects={projects} onProjectsChanged={loadProjects} />
        : tab === "sharepoint"
          ? <SharePointBrowser key="sp" projectId={projectId} projects={projects} />
          : tab === "archiv"
            ? <SharePointBrowser key="archiv" projectId={projectId} projects={projects} rootPath="Content-Archiv" rootLabel="Archiv" />
            : <FreigabenTab projectId={projectId} projects={projects} />}
    </div>
  );
}

export default function Dateien() {
  return (
    <div data-testid="dateien-page" className="w-full min-w-0 max-w-4xl p-4 sm:p-6 md:p-8">
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-orange-600 flex items-center gap-1.5">
        <FolderOpen className="h-3.5 w-3.5" /> Dateien
      </p>
      <h1 className="font-head text-3xl font-bold text-neutral-900 tracking-tight mt-1">Ablage, Projekte & Kunden-Freigaben</h1>
      <p className="text-sm text-neutral-500 mt-1 mb-6">
        Team-Cloud für Videos, Fotos und Dokumente — nach Kundenprojekten organisiert, im Chat teilbar oder per Link an externe Kunden zur Freigabe.
      </p>
      <DateienPanel />
    </div>
  );
}
